/**
 * Typed wrappers around the API Gateway backend.
 *
 * Every call checks res.ok and throws a descriptive ApiError instead of
 * silently yielding `undefined` (the legacy bug). The S3 PUT accepts any 2xx
 * (S3 commonly returns 200 or 204).
 */
import { API_ENDPOINTS } from '../config/awsConfig.ts';
import { getToken, onUnauthorized } from '../state/authStore.ts';
import type { S3File } from './types.ts';

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** Build request headers, attaching the Google bearer token when signed in. */
function authHeaders(extra?: Record<string, string>): Record<string, string> {
  const headers: Record<string, string> = { ...extra };
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

/** A 401 means the token is missing/expired — force re-authentication. */
function checkAuth(status: number): void {
  if (status === 401) onUnauthorized();
}

async function postJson<T>(url: string, body: unknown, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(body),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(`Network error calling ${url}: ${(err as Error).message}`);
  }
  if (!res.ok) {
    checkAuth(res.status);
    throw new ApiError(`${url} responded ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

export interface ListFilesResult {
  files: S3File[];
  commonPrefixes: string[];
  nextContinuationToken?: string;
}

export interface ListFilesParams {
  prefix?: string;
  maxKeys?: number;
  continuationToken?: string;
  /** Set '/' to also return event folders as commonPrefixes (needs updated Lambda). */
  delimiter?: string;
  signal?: AbortSignal;
}

/** GET /list-files — one page. Loop on nextContinuationToken to exhaust. */
export async function listFiles({
  prefix = '',
  maxKeys = 1000,
  continuationToken,
  delimiter,
  signal,
}: ListFilesParams): Promise<ListFilesResult> {
  const url = new URL(API_ENDPOINTS.LIST_FILES);
  url.searchParams.set('prefix', prefix);
  url.searchParams.set('maxKeys', String(maxKeys));
  if (continuationToken) url.searchParams.set('continuationToken', continuationToken);
  if (delimiter) url.searchParams.set('delimiter', delimiter);

  let res: Response;
  try {
    res = await fetch(url, { signal, headers: authHeaders() });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(`Network error listing files: ${(err as Error).message}`);
  }
  if (!res.ok) {
    checkAuth(res.status);
    throw new ApiError(`list-files responded ${res.status}`, res.status);
  }

  const data = (await res.json()) as {
    files?: S3File[];
    commonPrefixes?: string[];
    nextContinuationToken?: string;
  };
  return {
    files: data.files ?? [],
    commonPrefixes: data.commonPrefixes ?? [],
    nextContinuationToken: data.nextContinuationToken,
  };
}

/** Exhaust all pages of a prefix listing into a single array. */
export async function listAllFiles(
  params: Omit<ListFilesParams, 'continuationToken'>,
): Promise<S3File[]> {
  const all: S3File[] = [];
  let token: string | undefined;
  do {
    const page = await listFiles({ ...params, continuationToken: token });
    all.push(...page.files);
    token = page.nextContinuationToken;
  } while (token);
  return all;
}

/** POST /get-upload-url — mint a presigned PUT URL for a full S3 key. */
export async function getUploadUrl(
  fileName: string,
  fileType: string,
  signal?: AbortSignal,
): Promise<string> {
  const data = await postJson<{ uploadURL: string }>(
    API_ENDPOINTS.UPLOAD_URL,
    { fileName, fileType },
    signal,
  );
  if (!data.uploadURL) throw new ApiError('upload URL missing in response');
  return data.uploadURL;
}

/** POST /get-download-url — mint a presigned GET URL for a key. */
export async function getDownloadUrl(key: string, signal?: AbortSignal): Promise<string> {
  const data = await postJson<{ downloadURL: string }>(
    API_ENDPOINTS.DOWNLOAD_URL,
    { key },
    signal,
  );
  if (!data.downloadURL) throw new ApiError('download URL missing in response');
  return data.downloadURL;
}

/** PUT bytes to a presigned S3 URL. Resolves on any 2xx. */
export async function putToS3(uploadUrl: string, body: Blob, contentType: string): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body,
  });
  if (!(res.status >= 200 && res.status < 300)) {
    throw new ApiError(`S3 PUT failed with ${res.status}`, res.status);
  }
}

export interface ShareLink {
  key: string;
  url: string;
}

/** POST /share-links — presigned GET URLs for sharing. */
export async function getShareLinks(keys: string[], signal?: AbortSignal): Promise<ShareLink[]> {
  const data = await postJson<{ shareLinks: ShareLink[] }>(
    API_ENDPOINTS.SHARE_LINKS,
    { keys },
    signal,
  );
  return data.shareLinks ?? [];
}

export interface MeResponse {
  email: string;
  name: string;
  role: 'admin' | 'member';
  events: string[] | null;
}

/** GET /me — current user's profile + accessible events (null events = admin/all). */
export async function getMe(signal?: AbortSignal): Promise<MeResponse> {
  let res: Response;
  try {
    res = await fetch(API_ENDPOINTS.ME, { signal, headers: authHeaders() });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(`Network error calling /me: ${(err as Error).message}`);
  }
  if (!res.ok) {
    checkAuth(res.status);
    throw new ApiError(`/me responded ${res.status}`, res.status);
  }
  return (await res.json()) as MeResponse;
}

/** Lightweight user row for the admin list (grants NOT included — see adminGetUser). */
export interface AdminUserSummary {
  email: string;
  name: string;
  role: 'admin' | 'member';
  /** Number of granted events, or 'all' for admins. */
  eventCount: number | 'all';
}

export interface AdminUsersPage {
  users: AdminUserSummary[];
  nextCursor: string | null;
}

/** One user's full detail (profile + granted events). */
export interface AdminUserDetail {
  email: string;
  name: string;
  role: 'admin' | 'member';
  events: string[];
}

async function getJson<T>(url: string, label: string, signal?: AbortSignal): Promise<T> {
  let res: Response;
  try {
    res = await fetch(url, { signal, headers: authHeaders() });
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err;
    throw new ApiError(`Network error calling ${label}: ${(err as Error).message}`);
  }
  if (!res.ok) {
    checkAuth(res.status);
    throw new ApiError(`${label} responded ${res.status}`, res.status);
  }
  return (await res.json()) as T;
}

/** GET /admin/users — paginated, lightweight (admin only). */
export async function adminListUsers(
  opts: { search?: string; cursor?: string | null; limit?: number } = {},
  signal?: AbortSignal,
): Promise<AdminUsersPage> {
  const url = new URL(API_ENDPOINTS.ADMIN_USERS);
  if (opts.search) url.searchParams.set('search', opts.search);
  if (opts.cursor) url.searchParams.set('cursor', opts.cursor);
  if (opts.limit) url.searchParams.set('limit', String(opts.limit));
  const data = await getJson<{ users?: AdminUserSummary[]; nextCursor?: string | null }>(
    url.toString(),
    '/admin/users',
    signal,
  );
  return { users: data.users ?? [], nextCursor: data.nextCursor ?? null };
}

/** GET /admin/user?email= — one user's profile + grants (admin only). */
export async function adminGetUser(email: string, signal?: AbortSignal): Promise<AdminUserDetail> {
  const url = new URL(API_ENDPOINTS.ADMIN_USER);
  url.searchParams.set('email', email);
  return getJson<AdminUserDetail>(url.toString(), '/admin/user', signal);
}

/**
 * POST /admin/access — grant/revoke access (admin only).
 * Single: adminSetAccess({ email, event, granted }).
 * Bulk:   adminSetAccess({ emails, events, granted }) — cartesian product.
 */
export async function adminSetAccess(
  args: {
    email?: string;
    event?: string;
    emails?: string[];
    events?: string[];
    granted: boolean;
  },
  signal?: AbortSignal,
): Promise<void> {
  await postJson(API_ENDPOINTS.ADMIN_ACCESS, args, signal);
}

export interface EventSummaryDto {
  name: string;
  count: number;
  coverThumbnailKey?: string;
  /** Earliest / latest photo date in the event (YYYY-MM-DD), derived from filenames. */
  dateFrom?: string;
  dateTo?: string;
}

/** GET /events — server-resolved, access-filtered event list (one call). */
export async function getEvents(signal?: AbortSignal): Promise<EventSummaryDto[]> {
  const data = await getJson<{ events?: EventSummaryDto[] }>(
    API_ENDPOINTS.EVENTS,
    '/events',
    signal,
  );
  return data.events ?? [];
}

export interface MapPoint {
  key: string;
  thumbnailKey: string;
  displayName: string;
  gps: { lat: number; lng: number };
}

export interface MapPointsResponse {
  points: MapPoint[];
  scanned: number;
  capped: boolean;
}

/**
 * GET /map-points — every accessible photo that has GPS, aggregated server-side.
 * Replaces the client's per-sidecar fan-out (silently timed out on mobile).
 */
export async function getMapPoints(signal?: AbortSignal): Promise<MapPointsResponse> {
  return getJson<MapPointsResponse>(API_ENDPOINTS.MAP_POINTS, '/map-points', signal);
}

/** POST /request-access — any signed-in user asks the admin for access. Idempotent. */
export async function requestAccess(signal?: AbortSignal): Promise<void> {
  await postJson(API_ENDPOINTS.REQUEST_ACCESS, {}, signal);
}

/** A pending access request, shown in the admin console. */
export interface AccessRequest {
  email: string;
  name: string;
  requestedAt: string;
}

/** GET /admin/requests — pending access requests (admin only). */
export async function adminListRequests(signal?: AbortSignal): Promise<AccessRequest[]> {
  const data = await getJson<{ requests?: AccessRequest[] }>(
    API_ENDPOINTS.ADMIN_REQUESTS,
    '/admin/requests',
    signal,
  );
  return data.requests ?? [];
}

export type ArchiveStatus =
  | 'pending'
  | 'building'
  | 'ready'
  | 'error'
  | 'canceling'
  | 'canceled';

export interface ArchiveJob {
  jobId: string;
  status: ArchiveStatus;
  total: number;
}

export interface ArchiveState {
  status: ArchiveStatus;
  total: number;
  done: number;
  fileName: string;
  downloadURL?: string;
  error?: string;
}

/**
 * POST /download-archive — start a server-built ZIP job for a whole event
 * ({event}) or an explicit set of originals ({keys}). Returns a job id to poll.
 */
export async function startArchive(
  args: { event?: string; keys?: string[] },
  signal?: AbortSignal,
): Promise<ArchiveJob> {
  return postJson<ArchiveJob>(API_ENDPOINTS.DOWNLOAD_ARCHIVE, args, signal);
}

/** GET /download-archive?id= — poll a job; when ready it carries a downloadURL. */
export async function getArchiveStatus(
  jobId: string,
  signal?: AbortSignal,
): Promise<ArchiveState> {
  const url = new URL(API_ENDPOINTS.DOWNLOAD_ARCHIVE);
  url.searchParams.set('id', jobId);
  return getJson<ArchiveState>(url.toString(), '/download-archive', signal);
}

/** POST /download-archive {jobId, cancel} — request cancellation of a running job. */
export async function cancelArchive(jobId: string, signal?: AbortSignal): Promise<void> {
  await postJson(API_ENDPOINTS.DOWNLOAD_ARCHIVE, { jobId, cancel: true }, signal);
}

export interface MovePair {
  source: string;
  destination: string;
}

export interface BulkResult {
  source: string;
  destination: string;
  status: string;
  error?: string;
}

/**
 * POST /bulk-operations {operation:'move'} — copy then delete source.
 * Requires the updated Lambda (server/lambda/handler.js). The legacy Lambda
 * only supports 'copy'; callers should surface a clear error if move is
 * unavailable.
 */
export async function bulkMove(pairs: MovePair[], signal?: AbortSignal): Promise<BulkResult[]> {
  const data = await postJson<{ results: BulkResult[] }>(
    API_ENDPOINTS.BULK_OPERATIONS,
    { operation: 'move', keys: pairs },
    signal,
  );
  return data.results ?? [];
}
